import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { IMG } from "@/lib/content";
import { api } from "@/lib/api";

// Snapshot the built-in default images once so overrides can be cleared/reset cleanly.
const DEFAULT_IMG = { ...IMG };

// English source string -> Arabic (Modern Standard Arabic).
// t() falls back to the English source if a translation is missing.
export const AR = {
  // Nav & CTAs
  "About": "من نحن",
  "Investment": "الاستثمار",
  "Partnerships": "الشراكات",
  "Saudi Arabia": "المملكة العربية السعودية",
  "Portfolio": "المحفظة",
  "Insights": "رؤى",
  "Contact": "تواصل معنا",
  "Start a Conversation": "ابدأ محادثة",
  "Explore Intersect": "اكتشف إنترسكت",
  "Partner With Us": "كن شريكاً معنا",
  "Present an Opportunity": "اعرض فرصة",
  "Discover Intersect": "اكتشف إنترسكت",
  "Explore Saudi Opportunities": "استكشف الفرص السعودية",
  "View Portfolio": "استعرض المحفظة",
  "Read Perspective": "اقرأ الرؤى",
  "Submit an Opportunity": "قدّم فرصة",
  "Submit Opportunity": "قدّم الفرصة",
  "Start the Conversation": "ابدأ المحادثة",
  "Return Home": "العودة للرئيسية",
  "Investment Strategy": "استراتيجية الاستثمار",
  "Explore Ventures": "استكشف المشاريع",
  "Our Partnership Model": "نموذج شراكتنا",

  // Header/misc
  "Capital · Partnerships · Opportunity · Saudi Arabia": "رأس المال · الشراكات · الفرص · المملكة العربية السعودية",
  "Navigate": "تصفّح",
  "Connect": "تواصل",
  "LinkedIn": "لينكدإن",
  "Email for updates": "بريدك للتحديثات",
  "Privacy": "الخصوصية",
  "Terms": "الشروط",
  "Cookie Policy": "سياسة ملفات الارتباط",
  "Building ventures, partnerships and relationships across Saudi Arabia and global markets.": "نبني المشاريع والشراكات والعلاقات في المملكة العربية السعودية والأسواق العالمية.",
  "© {year} Intersect Holding. All rights reserved.": "© {year} إنترسكت القابضة. جميع الحقوق محفوظة.",

  // Hero
  "Where Capital, Opportunity & Partnerships Intersect.": "حيث يلتقي رأس المال والفرص والشراكات.",
  "Intersect Holding builds ventures, creates strategic partnerships and connects capital with opportunities across Saudi Arabia and international markets.": "تبني إنترسكت القابضة المشاريع، وتصنع الشراكات الاستراتيجية، وتربط رأس المال بالفرص في المملكة العربية السعودية والأسواق الدولية.",
  "Scroll": "مرّر للأسفل",

  // Philosophy
  "Intersect Philosophy": "فلسفة إنترسكت",
  "We operate at the intersection of capital, relationships and opportunity.": "نعمل عند نقطة تقاطع رأس المال والعلاقات والفرص.",
  "CAPITAL": "رأس المال",
  "VENTURES": "المشاريع",
  "PARTNERSHIPS": "الشراكات",
  "SAUDI ARABIA": "المملكة العربية السعودية",
  "Connecting investment capital with credible opportunities.": "ربط رأس المال الاستثماري بالفرص الموثوقة.",
  "Building, backing and scaling businesses with long-term potential.": "بناء ودعم وتوسيع الشركات ذات الإمكانات طويلة الأمد.",
  "Creating commercial alliances that expand capabilities and market access.": "خلق تحالفات تجارية توسّع القدرات وتفتح الأسواق.",
  "Providing access to one of the world's most dynamic economic transformations.": "إتاحة الوصول إلى واحد من أكثر التحولات الاقتصادية ديناميكية في العالم.",

  // About intro (home)
  "Intersect Holding": "إنترسكت القابضة",
  "More than capital.": "أكثر من مجرد رأس مال.",
  "Intersect Holding is a Saudi investment and strategic holding company developing ventures, partnerships and long-term commercial relationships across Saudi Arabia and international markets.": "إنترسكت القابضة شركة استثمار وقابضة استراتيجية سعودية تطوّر المشاريع والشراكات والعلاقات التجارية طويلة الأمد في المملكة العربية السعودية والأسواق الدولية.",
  "We identify opportunities where capital, relationships, technology, market access and execution can create sustainable value.": "نحدد الفرص التي يمكن فيها لرأس المال والعلاقات والتقنية والوصول إلى الأسواق والتنفيذ أن تصنع قيمة مستدامة.",

  // What we do
  "What We Do": "ما نقوم به",
  "Venture Capital": "رأس المال الجريء",
  "Venture Building": "بناء المشاريع",
  "Strategic Partnerships": "الشراكات الاستراتيجية",
  "Saudi Market Expansion": "التوسع في السوق السعودي",
  "Investing in ideas with the potential to scale.": "الاستثمار في الأفكار القابلة للنمو والتوسع.",
  "Turning opportunities into companies.": "تحويل الفرص إلى شركات.",
  "Relationships that create enterprise value.": "علاقات تصنع قيمة مؤسسية.",
  "Connecting global capability with Saudi opportunity.": "ربط القدرات العالمية بالفرص السعودية.",
  "We identify, evaluate and participate in high-potential ventures where strategic capital, Saudi market access and partnership development can accelerate growth.": "نحدد ونقيّم ونشارك في المشاريع الواعدة حيث يمكن لرأس المال الاستراتيجي والوصول إلى السوق السعودي وتطوير الشراكات أن يسرّع النمو.",
  "Intersect develops ventures around validated market opportunities, assembling the capital, partnerships, operating capabilities and commercial relationships necessary for growth.": "تطوّر إنترسكت المشاريع حول فرص سوقية مؤكدة، وتجمع رأس المال والشراكات والقدرات التشغيلية والعلاقات التجارية اللازمة للنمو.",
  "We bring together organizations with complementary capabilities to create strategic alliances, joint ventures and commercial partnerships.": "نجمع بين المؤسسات ذات القدرات المتكاملة لخلق تحالفات استراتيجية ومشاريع مشتركة وشراكات تجارية.",
  "Intersect works with international companies, investors and innovators seeking strategic relationships, commercial opportunities and long-term participation in the Saudi economy.": "تعمل إنترسكت مع الشركات والمستثمرين والمبتكرين الدوليين الباحثين عن علاقات استراتيجية وفرص تجارية ومشاركة طويلة الأمد في الاقتصاد السعودي.",

  // Saudi opportunity
  "A market being built for the next generation.": "سوق يُبنى للأجيال القادمة.",
  "Saudi Arabia is undergoing one of the world's most ambitious economic transformations. New industries, investment platforms, technologies and commercial ecosystems are being created across the Kingdom.": "تشهد المملكة العربية السعودية واحداً من أكثر التحولات الاقتصادية طموحاً في العالم. تُبنى صناعات ومنصات استثمارية وتقنيات ومنظومات تجارية جديدة في أنحاء المملكة.",
  "Intersect identifies where these changes create opportunities for investment, partnerships and new ventures.": "تحدد إنترسكت المواضع التي تخلق فيها هذه التغيرات فرصاً للاستثمار والشراكات والمشاريع الجديدة.",
  "Investment Opportunity": "فرصة استثمارية",
  "Strategic Sectors": "قطاعات استراتيجية",
  "Transformation Horizon": "أفق التحول",

  // Areas we explore
  "Areas We Explore": "مجالات نستكشفها",
  "Sectors we actively study for investment, partnership and venture-building potential.": "قطاعات ندرسها بفاعلية بحثاً عن إمكانات الاستثمار والشراكة وبناء المشاريع.",
  "Artificial Intelligence": "الذكاء الاصطناعي",
  "Financial Technology": "التقنية المالية",
  "Industrial Development": "التنمية الصناعية",
  "Advanced Manufacturing": "التصنيع المتقدم",
  "Hospitality": "الضيافة",
  "Sports": "الرياضة",
  "Healthcare": "الرعاية الصحية",
  "Life Sciences": "علوم الحياة",
  "Energy": "الطاقة",
  "Clean Technology": "التقنية النظيفة",
  "Real Estate": "العقارات",
  "Logistics": "الخدمات اللوجستية",
  "Digital Infrastructure": "البنية التحتية الرقمية",
  "Consumer": "المستهلك",
  "Food Technology": "تقنية الغذاء",
  "Mobility": "التنقل",
  "Creative Industries": "الصناعات الإبداعية",

  // Partnerships (home)
  "The right partnership can create an entirely new market.": "الشراكة الصحيحة قادرة على خلق سوق جديد بالكامل.",
  "We work across borders and sectors to identify organizations whose capabilities become more valuable when combined.": "نعمل عبر الحدود والقطاعات لتحديد المؤسسات التي تزداد قيمة قدراتها عند دمجها.",
  "International Company": "شركة دولية",
  "Saudi Market Partner": "شريك في السوق السعودي",
  "Investment Capital": "رأس مال استثماري",
  "Local Market Access": "الوصول للسوق المحلي",
  "Joint Venture · Strategic Partnership · New Venture": "مشروع مشترك · شراكة استراتيجية · مشروع جديد",

  // Investment approach
  "Investment Approach": "منهج الاستثمار",
  "How we think about investment.": "كيف نفكّر في الاستثمار.",
  "Strategic Value": "القيمة الاستراتيجية",
  "Market Access": "الوصول إلى الأسواق",
  "Partnership": "الشراكة",
  "Execution": "التنفيذ",
  "Long-Term Thinking": "التفكير طويل الأمد",
  "Capital should create more than financial participation.": "ينبغي لرأس المال أن يخلق أكثر من مجرد مشاركة مالية.",
  "Strong companies become stronger when they gain access to the right markets.": "تزداد الشركات القوية قوةً حين تصل إلى الأسواق المناسبة.",
  "Relationships can be one of the most valuable forms of capital.": "قد تكون العلاقات من أثمن أشكال رأس المال.",
  "Opportunities only create value when they can be executed.": "لا تصنع الفرص قيمة إلا عندما يمكن تنفيذها.",
  "We seek durable businesses rather than short-term transactions.": "نسعى إلى شركات مستدامة لا إلى صفقات قصيرة الأمد.",

  // Portfolio
  "Portfolio / Ventures": "المحفظة / المشاريع",
  "Building at the intersection.": "نبني عند نقطة التقاطع.",
  "Our portfolio is taking shape.": "محفظتنا في طور التكوين.",
  "Intersect is actively evaluating ventures, investment opportunities and strategic partnerships.": "تقوم إنترسكت بتقييم المشاريع والفرص الاستثمارية والشراكات الاستراتيجية بفاعلية.",

  // Network
  "Relationship Network": "شبكة العلاقات",
  "Relationships are infrastructure.": "العلاقات بنية تحتية.",
  "Capital alone rarely builds transformative companies.": "نادراً ما يبني رأس المال وحده شركات تحويلية.",
  "Intersect develops relationships across investors, founders, companies, institutions and markets to help create opportunities that would otherwise remain disconnected.": "تطوّر إنترسكت علاقات مع المستثمرين والمؤسِّسين والشركات والمؤسسات والأسواق للمساعدة في خلق فرص كانت ستبقى منفصلة لولا ذلك.",
  "Investors": "المستثمرون",
  "Founders": "المؤسِّسون",
  "Corporates": "الشركات",
  "Family Offices": "المكاتب العائلية",
  "Institutions": "المؤسسات",
  "Technology": "التقنية",
  "Global Markets": "الأسواق العالمية",
  "INTERSECT": "إنترسكت",

  // Insights
  "Perspective": "رؤى",
  "Insights on capital, partnerships and Saudi opportunity.": "رؤى حول رأس المال والشراكات والفرص السعودية.",
  "Perspectives from Intersect on investment, markets and the Saudi economy.": "رؤى من إنترسكت حول الاستثمار والأسواق والاقتصاد السعودي.",

  // Final CTA
  "Great opportunities often begin with a conversation.": "غالباً ما تبدأ الفرص العظيمة بمحادثة.",
  "conversation.": "بمحادثة.",
  "Whether you are building a company, exploring Saudi Arabia, seeking investment or looking for the right strategic partner, we would like to hear from you.": "سواء كنت تبني شركة، أو تستكشف المملكة العربية السعودية، أو تبحث عن استثمار، أو تبحث عن الشريك الاستراتيجي المناسب، يسعدنا أن نسمع منك.",

  // About page
  "Building value through capital, relationships and execution.": "بناء القيمة عبر رأس المال والعلاقات والتنفيذ.",
  "Who We Are": "من نحن",
  "A Saudi investment and strategic holding company.": "شركة استثمار وقابضة استراتيجية سعودية.",
  "Intersect Holding develops ventures, partnerships and long-term commercial relationships across Saudi Arabia and international markets. We are positioned as an investor, partner, venture builder and originator of opportunity.": "تطوّر إنترسكت القابضة المشاريع والشراكات والعلاقات التجارية طويلة الأمد في المملكة العربية السعودية والأسواق الدولية. نحن مستثمر وشريك وبانٍ للمشاريع وصانع للفرص.",
  "We are not a consultancy. We participate, build and hold — creating value where capital, markets, relationships and ideas intersect.": "نحن لسنا شركة استشارات. نحن نشارك ونبني ونحتفظ — نصنع القيمة حيث يتقاطع رأس المال والأسواق والعلاقات والأفكار.",
  "Our Purpose": "غايتنا",
  "Identifying where value can be created.": "تحديد المواضع التي يمكن فيها خلق القيمة.",
  "Intersect exists to identify points where capital, markets, relationships and ideas can intersect to create new economic value.": "وُجدت إنترسكت لتحديد النقاط التي يمكن أن يتقاطع فيها رأس المال والأسواق والعلاقات والأفكار لخلق قيمة اقتصادية جديدة.",
  "Our Philosophy": "فلسفتنا",
  "We believe the most valuable companies are built at intersections — where capital meets opportunity, where global capability meets Saudi ambition, and where relationships become partnerships.": "نؤمن أن أثمن الشركات تُبنى عند التقاطعات — حيث يلتقي رأس المال بالفرصة، والقدرة العالمية بالطموح السعودي، وحيث تتحول العلاقات إلى شراكات.",
  "Our approach is patient and long-term. We seek durable businesses rather than short-term transactions.": "منهجنا صبور وطويل الأمد. نسعى إلى شركات مستدامة لا إلى صفقات قصيرة الأمد.",
  "Our Values": "قيمنا",
  "The principles that guide us.": "المبادئ التي توجّهنا.",
  "Relationships": "العلاقات",
  "Integrity": "النزاهة",
  "Entrepreneurship": "ريادة الأعمال",
  "Curiosity": "الفضول",
  "Saudi Roots. Global Perspective.": "جذور سعودية. رؤية عالمية.",
  "Grounded in Saudi Arabia, connected to the world.": "متجذرون في المملكة العربية السعودية، متصلون بالعالم.",
  "Our network spans investors, founders, corporations, family offices and institutions — the relationships that turn opportunities into companies.": "تمتد شبكتنا لتشمل المستثمرين والمؤسِّسين والشركات والمكاتب العائلية والمؤسسات — العلاقات التي تحوّل الفرص إلى شركات.",

  // Investment page
  "Backing businesses with more than capital.": "ندعم الشركات بأكثر من رأس المال.",
  "Capital paired with market access, relationships and the ability to execute.": "رأس مال مقترن بالوصول إلى الأسواق والعلاقات والقدرة على التنفيذ.",
  "Investment Philosophy": "فلسفة الاستثمار",
  "Our value beyond capital.": "قيمتنا التي تتجاوز رأس المال.",
  "We identify, evaluate and participate in high-potential ventures where strategic capital, Saudi market access and partnership development can accelerate growth. Strong companies become stronger when they gain access to the right markets and relationships.": "نحدد ونقيّم ونشارك في المشاريع الواعدة حيث يمكن لرأس المال الاستراتيجي والوصول إلى السوق السعودي وتطوير الشراكات أن يسرّع النمو. تزداد الشركات القوية قوةً حين تصل إلى الأسواق والعلاقات المناسبة.",
  "Investment Themes & Stages": "محاور ومراحل الاستثمار",
  "Where we participate.": "أين نشارك.",
  "Early Stage": "المرحلة المبكرة",
  "Growth Stage": "مرحلة النمو",
  "Strategic Investments": "الاستثمارات الاستراتيجية",
  "Joint Ventures": "المشاريع المشتركة",
  "Corporate Ventures": "المشاريع المؤسسية",
  "Special Situations": "الحالات الخاصة",
  "Backing founders at the formation of ambitious companies.": "دعم المؤسِّسين عند تأسيس شركات طموحة.",
  "Supporting proven businesses entering their scaling phase.": "دعم الشركات المثبتة عند دخولها مرحلة التوسع.",
  "Capital paired with market access and relationships.": "رأس مال مقترن بالوصول إلى الأسواق والعلاقات.",
  "Co-building new companies around validated opportunities.": "المشاركة في بناء شركات جديدة حول فرص مؤكدة.",
  "Partnering with corporates to create new business lines.": "الشراكة مع الشركات لخلق خطوط أعمال جديدة.",
  "Selective participation where structure creates value.": "مشاركة انتقائية حيث يصنع الهيكل القيمة.",
  "What We Look For": "ما الذي نبحث عنه",
  "Investment criteria.": "معايير الاستثمار.",
  "Strong founders": "مؤسِّسون أقوياء",
  "Large addressable markets": "أسواق مستهدفة كبيرة",
  "Saudi market relevance": "ملاءمة للسوق السعودي",
  "Scalable business models": "نماذج أعمال قابلة للتوسع",
  "Technology differentiation": "تميّز تقني",
  "Strategic partnership potential": "إمكانات شراكة استراتيجية",
  "Regional expansion opportunity": "فرصة توسّع إقليمي",
  "Clear value creation strategy": "استراتيجية واضحة لخلق القيمة",
  "Our Value Beyond Capital": "قيمتنا التي تتجاوز رأس المال",

  // Partnerships page
  "Some opportunities require more than one company.": "بعض الفرص تتطلب أكثر من شركة واحدة.",
  "We bring together organizations whose capabilities become more valuable when combined.": "نجمع بين المؤسسات التي تزداد قيمة قدراتها عند دمجها.",
  "Our Role": "دورنا",
  "Creating alliances that create value.": "خلق تحالفات تصنع القيمة.",
  "Intersect identifies organizations with complementary capabilities and aligns their interests to create strategic alliances, joint ventures and commercial partnerships. We work across borders and sectors — connecting global capability with Saudi opportunity.": "تحدد إنترسكت المؤسسات ذات القدرات المتكاملة وتوائم بين مصالحها لخلق تحالفات استراتيجية ومشاريع مشتركة وشراكات تجارية. نعمل عبر الحدود والقطاعات — نربط القدرة العالمية بالفرصة السعودية.",
  "Partnership Models": "نماذج الشراكة",
  "The forms partnership can take.": "الأشكال التي قد تتخذها الشراكة.",
  "Strategic Alliances": "تحالفات استراتيجية",
  "Market Partnerships": "شراكات سوقية",
  "Technology Partnerships": "شراكات تقنية",
  "Distribution Partnerships": "شراكات توزيع",
  "Investment Partnerships": "شراكات استثمارية",
  "Government & Institutional Collaboration": "التعاون الحكومي والمؤسسي",
  "Cross-Border Partnerships": "شراكات عابرة للحدود",
  "The Partnership Model": "نموذج الشراكة",
  "From opportunity to execution.": "من الفرصة إلى التنفيذ.",
  "Opportunity": "الفرصة",
  "Identify Strategic Participants": "تحديد المشاركين الاستراتيجيين",
  "Align Interests": "مواءمة المصالح",
  "Define Commercial Model": "تحديد النموذج التجاري",
  "Build Partnership": "بناء الشراكة",
  "Execute": "التنفيذ",

  // Saudi page
  "Saudi Arabia is becoming a platform for global growth.": "تتحول المملكة العربية السعودية إلى منصة للنمو العالمي.",
  "One of the world's most ambitious economic transformations — and a growing intersection of global markets.": "واحد من أكثر التحولات الاقتصادية طموحاً في العالم — ونقطة تقاطع متنامية للأسواق العالمية.",
  "Areas of Focus": "مجالات التركيز",
  "Economic transformation": "التحول الاقتصادي",
  "Investment environment": "بيئة الاستثمار",
  "New industries": "صناعات جديدة",
  "Infrastructure": "البنية التحتية",
  "Industrial development": "التنمية الصناعية",
  "Tourism": "السياحة",
  "Entertainment": "الترفيه",
  "Capital formation": "تكوين رأس المال",
  "International participation": "المشاركة الدولية",
  "Economic & Opportunity Centers": "مراكز الاقتصاد والفرص",
  "A Kingdom of expanding centers.": "مملكة من المراكز المتنامية.",
  "Economic activity is expanding across multiple regions of Saudi Arabia, each developing its own industries and opportunities.": "يتوسع النشاط الاقتصادي عبر مناطق متعددة في المملكة العربية السعودية، وتطوّر كل منها صناعاتها وفرصها الخاصة.",
  "Riyadh": "الرياض",
  "Jeddah": "جدة",
  "Eastern Province": "المنطقة الشرقية",
  "NEOM / Northwest": "نيوم / الشمال الغربي",
  "Makkah Region": "منطقة مكة المكرمة",
  "Madinah": "المدينة المنورة",
  "Global Connection": "الاتصال العالمي",
  "Saudi Arabia at the intersection of global markets.": "المملكة العربية السعودية عند نقطة تقاطع الأسواق العالمية.",
  "North America": "أمريكا الشمالية",
  "Europe": "أوروبا",
  "Asia": "آسيا",
  "Middle East": "الشرق الأوسط",
  "Africa": "أفريقيا",
  "Saudi Opportunity": "الفرصة السعودية",
  "Emerging Industries": "الصناعات الناشئة",
  "Market Entry Through Partnership": "دخول السوق عبر الشراكة",
  "Building Local Relationships": "بناء العلاقات المحلية",
  "Long-Term Participation": "المشاركة طويلة الأمد",
  "New industries, investment platforms and commercial ecosystems are being created across the Kingdom, opening opportunities for investment, partnership and venture building.": "تُبنى صناعات ومنصات استثمارية ومنظومات تجارية جديدة في أنحاء المملكة، وتفتح فرصاً للاستثمار والشراكة وبناء المشاريع.",
  "From technology and financial services to tourism, entertainment and advanced manufacturing, entire sectors are being formed.": "من التقنية والخدمات المالية إلى السياحة والترفيه والتصنيع المتقدم، تتشكل قطاعات بأكملها.",
  "For international companies, the most effective route into Saudi Arabia is often through the right local partner and structure.": "بالنسبة للشركات الدولية، غالباً ما يكون الطريق الأنجع إلى المملكة عبر الشريك المحلي المناسب والهيكل الصحيح.",
  "Relationships with investors, founders, corporates and institutions are essential to creating durable value in the Kingdom.": "العلاقات مع المستثمرين والمؤسِّسين والشركات والمؤسسات أساسية لخلق قيمة مستدامة في المملكة.",
  "Intersect seeks long-term participation in the Saudi economy — building companies rather than transactions.": "تسعى إنترسكت إلى مشاركة طويلة الأمد في الاقتصاد السعودي — بناء شركات لا صفقات.",

  // Portfolio page
  "Investments, ventures, partnerships and joint ventures developed by Intersect.": "استثمارات ومشاريع وشراكات ومشاريع مشتركة طوّرتها إنترسكت.",
  "All": "الكل",
  "Venture": "مشروع",
  "Loading…": "جارٍ التحميل…",
  "Sector": "القطاع",
  "Geography": "الموقع الجغرافي",
  "Year": "السنة",
  "Status": "الحالة",
  "Company Overview": "نبذة عن الشركة",
  "Intersect Role": "دور إنترسكت",
  "Visit Website": "زيارة الموقع",
  "Company not found.": "الشركة غير موجودة.",
  "Back to Portfolio": "العودة إلى المحفظة",

  // Insights page
  "Markets": "الأسواق",
  "Industry": "الصناعة",
  "No perspectives in this category yet.": "لا توجد رؤى في هذه الفئة بعد.",
  "Article not found.": "المقال غير موجود.",
  "Back to Insights": "العودة إلى الرؤى",

  // Contact page
  "Start a Conversation.": "ابدأ محادثة.",
  "Whether you are seeking investment, exploring Saudi Arabia or looking for a partner, we would like to hear from you.": "سواء كنت تبحث عن استثمار، أو تستكشف المملكة العربية السعودية، أو تبحث عن شريك، يسعدنا أن نسمع منك.",
  "I am…": "أنا…",
  "Seeking Investment": "أبحث عن استثمار",
  "Presenting an Investment Opportunity": "أعرض فرصة استثمارية",
  "Exploring Saudi Arabia": "أستكشف المملكة العربية السعودية",
  "Looking for a Saudi Partner": "أبحث عن شريك سعودي",
  "Looking for an International Partner": "أبحث عن شريك دولي",
  "Exploring a Joint Venture": "أستكشف مشروعاً مشتركاً",
  "Representing an Investment Fund": "أمثّل صندوق استثمار",
  "Representing a Family Office": "أمثّل مكتباً عائلياً",
  "Other": "أخرى",
  "First Name": "الاسم الأول",
  "Last Name": "اسم العائلة",
  "Company": "الشركة",
  "Position": "المنصب",
  "Email": "البريد الإلكتروني",
  "Phone": "الهاتف",
  "Country": "الدولة",
  "Organization Type": "نوع المؤسسة",
  "Area of Interest": "مجال الاهتمام",
  "Message": "الرسالة",
  "Thank you.": "شكراً لك.",
  "Your message has been received. A member of the Intersect team will be in touch.": "تم استلام رسالتك. سيتواصل معك أحد أعضاء فريق إنترسكت.",
  "Sending…": "جارٍ الإرسال…",
  "Investor": "مستثمر",
  "VC / PE Firm": "شركة رأس مال جريء / ملكية خاصة",
  "Startup / Founder": "شركة ناشئة / مؤسِّس",
  "Corporate": "شركة",
  "Government / Institution": "جهة حكومية / مؤسسة",
  "Saudi Market Entry": "دخول السوق السعودي",

  // Opportunity page
  "Present an opportunity to Intersect.": "اعرض فرصة على إنترسكت.",
  "Share your company, venture or partnership opportunity. Submissions are stored securely and reviewed by our team.": "شارك شركتك أو مشروعك أو فرصة الشراكة. تُحفظ الطلبات بأمان ويراجعها فريقنا.",
  "Your opportunity has been received. We review every submission and will reach out if there is a fit.": "تم استلام فرصتك. نراجع كل طلب وسنتواصل معك إن كان هناك توافق.",
  "Submitting…": "جارٍ الإرسال…",
  "Website": "الموقع الإلكتروني",
  "Founder Name": "اسم المؤسِّس",
  "Founder Email": "بريد المؤسِّس",
  "Headquarters": "المقر الرئيسي",
  "Company Stage": "مرحلة الشركة",
  "Funding Stage": "مرحلة التمويل",
  "Amount Being Raised": "المبلغ المطلوب جمعه",
  "Current Revenue": "الإيرادات الحالية",
  "Saudi Market Presence": "الحضور في السوق السعودي",
  "Saudi Expansion Interest": "الاهتمام بالتوسع في السعودية",
  "Investment Summary": "ملخص الاستثمار",
  "Strategic Partnership Opportunity": "فرصة شراكة استراتيجية",
  "Why Intersect?": "لماذا إنترسكت؟",
  "Pitch Deck (PDF, max 25MB)": "العرض التقديمي (PDF، بحد أقصى 25 ميجابايت)",

  // 404 / legal
  "This intersection doesn't exist.": "هذا التقاطع غير موجود.",
  "The page you are looking for could not be found.": "تعذّر العثور على الصفحة التي تبحث عنها.",
  "Legal": "قانوني",
  "Relationship": "نوع العلاقة",
  "Privacy Policy": "سياسة الخصوصية",
  "Terms of Use": "شروط الاستخدام",
  "I consent to Intersect Holding storing and processing my information in order to respond to my enquiry.": "أوافق على قيام إنترسكت القابضة بتخزين ومعالجة معلوماتي للرد على استفساري.",
  "Verification: what is": "التحقق: كم يساوي",
};

const LanguageContext = createContext(null);

const PARAMS = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
export const IS_PREVIEW = PARAMS.get("preview") === "1";

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => PARAMS.get("lang") || (typeof window !== "undefined" && localStorage.getItem("intersect_lang")) || "en");
  const [overrides, setOverrides] = useState({ text_en: {}, text_ar: {}, images: {} });

  const apply = useCallback((data) => {
    // Apply image overrides onto the shared IMG object (defaults first, then overrides).
    Object.assign(IMG, DEFAULT_IMG, data.images || {});
    setOverrides({ text_en: data.text_en || {}, text_ar: data.text_ar || {}, images: data.images || {} });
  }, []);

  useEffect(() => {
    let active = true;
    api.get("/content", { params: IS_PREVIEW ? { draft: true } : {} }).then(({ data }) => {
      if (active) apply(data);
    }).catch(() => {});
    return () => { active = false; };
  }, [apply]);

  // Live preview: the admin editor pushes unsaved content into this iframe.
  useEffect(() => {
    if (!IS_PREVIEW) return;
    const onMsg = (e) => {
      const m = e.data;
      if (m && m.type === "intersect-preview") {
        apply(m.payload || {});
        if (m.lang) setLang(m.lang);
      }
    };
    window.addEventListener("message", onMsg);
    window.parent?.postMessage({ type: "intersect-preview-ready" }, "*");
    return () => window.removeEventListener("message", onMsg);
  }, [apply]);

  useEffect(() => {
    const dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.setAttribute("lang", lang);
    if (!IS_PREVIEW) localStorage.setItem("intersect_lang", lang);
  }, [lang]);

  const t = useCallback((s) => {
    if (s == null) return s;
    if (lang === "ar") {
      return overrides.text_ar[s] || AR[s] || s;
    }
    return overrides.text_en[s] || s;
  }, [lang, overrides]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, isAr: lang === "ar" }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
